import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND, NAV } from "@/lib/site";
import { Menu, X, HeartHandshake } from "lucide-react";

export default function Header({ onDonate }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  const handleAnchor = (e, href) => {
    if (isHome) {
      // smooth scroll handled by browser via anchor
      return;
    }
    e.preventDefault();
    navigate("/" + href); // /#about etc.
    close();
  };

  const renderNavLink = (item, isMobile = false) => {
    const baseClass = isMobile
      ? "text-base text-brand-ink py-2 border-b border-brand-border/70"
      : "text-[15px] text-brand-ink/85 hover:text-brand-forest underline-link transition-colors";
    const testId = `${isMobile ? "mobile-" : ""}nav-${item.label
      .toLowerCase()
      .replace(/\s+/g, "-")}`;

    if (item.type === "route") {
      return (
        <Link
          key={item.href}
          to={item.href}
          data-testid={testId}
          onClick={close}
          className={baseClass}
        >
          {item.label}
        </Link>
      );
    }
    // anchor link
    return (
      <a
        key={item.href}
        href={isHome ? item.href : "/" + item.href}
        data-testid={testId}
        onClick={(e) => {
          handleAnchor(e, item.href);
          if (isMobile) close();
        }}
        className={baseClass}
      >
        {item.label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-brand-cream/90 backdrop-blur-xl border-b border-brand-border"
          : "bg-brand-cream/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 sm:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 sm:gap-4"
          onClick={close}
        >
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${BRAND.logoIcon}`}
            alt="Nguvu Ya Mwanamke Foundation"
            className="h-12 sm:h-14 w-auto object-contain"
            onError={(e) => { e.currentTarget.src = BRAND.logo; }}
          />
          <div className="hidden xs:block h-8 sm:h-10 w-px bg-brand-gold" />
          <div className="leading-tight">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-brand-forest font-semibold">
              Established · Dar es Salaam
            </div>
            <div className="font-serif italic text-[17px] sm:text-[22px] text-brand-ink tracking-tight mt-0.5 sm:mt-1 whitespace-nowrap">
              Nguvu Ya Mwanamke
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => renderNavLink(item))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <button
              onClick={onDonate}
              data-testid="donate-nav-button"
              className="btn-primary text-sm px-5 py-2.5"
            >
              <HeartHandshake className="h-4 w-4" />
              Donate
            </button>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-border text-brand-ink hover:bg-brand-sand transition"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-brand-cream border-t border-brand-border"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => renderNavLink(item, true))}
            <button
              onClick={() => {
                onDonate();
                close();
              }}
              data-testid="donate-mobile-button"
              className="btn-primary mt-2 w-full"
            >
              <HeartHandshake className="h-4 w-4" />
              Donate
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
